import { useCallback, useEffect, useRef, useState } from "react";

const SOUND_KEY = "pixley-math-castle-sound";

const PATTERNS = {
  correct: [[523, 0], [659, 0.08], [784, 0.16]],
  wrong: [[220, 0], [170, 0.1]],
  blast: [[330, 0], [660, 0.045], [990, 0.09]],
  breach: [[180, 0], [130, 0.12], [90, 0.24]],
  victory: [[523, 0], [659, 0.12], [784, 0.24], [1046, 0.38]],
};

export const useCastleSoundManager = () => {
  const audioRef = useRef(null);
  const [enabled, setEnabled] = useState(() => {
    try {
      return window.localStorage.getItem(SOUND_KEY) !== "off";
    } catch {
      return true;
    }
  });

  useEffect(() => () => audioRef.current?.close?.(), []);

  const play = useCallback(
    (name) => {
      if (!enabled || !PATTERNS[name]) return;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      if (!audioRef.current) audioRef.current = new AudioContext();
      const context = audioRef.current;
      if (context.state === "suspended") context.resume();
      const now = context.currentTime;

      PATTERNS[name].forEach(([frequency, delay], index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = name === "breach" || name === "glove" ? "triangle" : "sine";
        oscillator.frequency.setValueAtTime(frequency, now + delay);
        gain.gain.setValueAtTime(0.0001, now + delay);
        gain.gain.exponentialRampToValueAtTime(0.055, now + delay + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.16 + index * 0.01);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(now + delay);
        oscillator.stop(now + delay + 0.19 + index * 0.01);
      });
    },
    [enabled]
  );

  const toggle = useCallback(() => {
    setEnabled((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(SOUND_KEY, next ? "on" : "off");
      } catch {
        // Sound preference is optional.
      }
      return next;
    });
  }, []);

  return { enabled, play, toggle };
};
